import { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import get from 'lodash/get';
import { SparklesIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { cn } from 'utils/shadcn';

function hasNonEmptyFieldValue(value: unknown): boolean {
  if (value == null) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  return false;
}

export function AIFilledHintDecoration({
  readOnly,
}: {
  readOnly?: boolean;
}): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap items-center text-xs font-medium text-accent-main',
        readOnly ? 'opacity-40' : 'opacity-75',
        'ai-hint-span w-[72px] max-w-[72px]'
      )}
    >
      <SparklesIcon className='h-3 w-3 mx-1 stroke-accent!' strokeWidth={2} />
      <span className='whitespace-nowrap text-xs ai-hint-text'>AI-filled</span>
    </span>
  );
}

export type AIHintProps = {
  // Form input name
  name: string;
  // Do not show AI sparkle icon or text. For example, we often default dates to now() so that should not be considered AI-filled
  hideAIHint?: boolean;
  // If container not wide enough to display both icon and text, show icon only
  // Should not be needed given `ai-hint-span` and `ai-hint-text` custom classes, bu
  aiIconOnly?: boolean;
  // If true, applies a transparent color to match read-only fields
  readOnly?: boolean;
  // Optional tooltip text shown when hovering over the AI hint
  aiHintTooltip?: string;
};

// Adds ~AI sparkle~ to the right of an input's label if there's a defaultValue.
// Visual cue that demonstrates Drumkit's usefulness to the user.
export default function AIHintLabel({
  name,
  hideAIHint,
  aiIconOnly,
  readOnly,
  aiHintTooltip,
}: AIHintProps): JSX.Element {
  const { aiDefaultValues, aiIconOnly: formAIIconOnly } =
    useExtendedFormContext();

  const {
    formState: { defaultValues, dirtyFields },
    getFieldState,
    getValues,
  } = useFormContext();

  const currentValue = getValues(name);
  const defaultValue = get(defaultValues, name);

  const isAutofillAndValid =
    aiDefaultValues &&
    !hideAIHint &&
    hasNonEmptyFieldValue(defaultValue) &&
    hasNonEmptyFieldValue(currentValue) &&
    !getFieldState(name).isDirty && // If the user has edited the field, then it's no longer AI-filled
    !get(dirtyFields, name); // Don't ask me why getFieldState works for some but not all inputs, namely select dropdowns ¯\_(ツ)_/¯,

  if (!isAutofillAndValid) {
    return <></>; // Return empty fragment to avoid rendering anything
  }

  const hintContent = (
    <span
      className={cn(
        'inline-flex whitespace-nowrap items-center text-xs font-medium text-accent-main',
        readOnly ? 'opacity-40' : 'opacity-75',
        aiIconOnly || formAIIconOnly
          ? 'shrink-0 w-[20px] max-w-[20px]'
          : 'ai-hint-span w-[72px] max-w-[72px]'
      )}
    >
      <SparklesIcon className='h-3 w-3 mx-1 stroke-accent!' strokeWidth={2} />
      {!aiIconOnly && !formAIIconOnly && (
        <span className='whitespace-nowrap text-xs ai-hint-text'>
          AI-filled
        </span>
      )}
    </span>
  );

  if (!aiHintTooltip) {
    return hintContent;
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{hintContent}</TooltipTrigger>
      <TooltipContent className='w-[240px] max-w-full ml-1'>
        <Typography
          variant='body-xs'
          className='whitespace-normal wrap-break-word'
        >
          {aiHintTooltip}
        </Typography>
      </TooltipContent>
    </Tooltip>
  );
}
